/*
Purpose: Paginated list view of user's analysis orders with expandable inline job previews.
Audience: Results shell delegates All Orders mode to this component.
Props:
- orders: Array<{ id, orderName, createdAt, providers, userId }>
- expandedOrders: Record<orderId, boolean>
- jobsByOrderId: Record<orderId, Array<Job>>
- resultsByJobId: Record<jobId, {...}>
- onToggleExpand(orderId): () => void
- onEnsureJobsLoaded(orderId): () => Promise<void>
- onOpenOrder(orderId): () => void   // navigate to single-order view
- onOpenJob(orderId, jobId): () => void // navigate to single-order view focusing a job
- hasMore: boolean
- isLoading: boolean
- onLoadMore: () => void
*/
import React, { useMemo, useRef, useState } from 'react';
import '../styles/OrdersListView.css';
// Icons removed - no longer needed after scroll tracking cleanup
import JobCard from '../../results/display/cards/JobCard';
import OrderCard from '../../results/display/cards/OrderCard';
import { buildOrderViewModel } from '../../results/utils/orderViewModel';
import { OrderCardSkeleton, JobsListSkeleton } from '../../common/SkeletonLoaders';
// Focus management hook removed - no longer needed after scroll cleanup

const OrdersListView = ({
  orders,
  expandedOrders,
  jobsByOrderId,
  resultsByJobId,
  onToggleExpand,
  onEnsureJobsLoaded,
  onOpenOrder,
  onOpenJob,
  hasMore,
  isLoading,
  onLoadMore,
  showFilterBar = true // New prop to control filter bar visibility
  // Note: initialScrollTop and onScrollTopChange removed - using full-window scroll now
}) => {
  // Filters and sorting (local to list view)
  const [providerFilter, setProviderFilter] = useState('all'); // 'all' | providerId substring match
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'processing' | 'completed' | 'failed'
  const [targetFilter, setTargetFilter] = useState('all'); // 'all' | target id
  const [sortBy, setSortBy] = useState('date_desc'); // 'date_desc' | 'date_asc' | 'jobs_desc' | 'jobs_asc' | 'cost_desc' | 'cost_asc'
  const [filtersExpanded, setFiltersExpanded] = useState(false); // Collapsible on mobile

  const providerOptions = useMemo(() => {
    const set = new Set();
    orders.forEach(o => (o.providers || []).forEach(p => set.add((p.providerId || '').toLowerCase())));
    return Array.from(set).filter(Boolean);
  }, [orders]);

  const targetOptions = useMemo(() => {
    const set = new Set();
    orders.forEach(o => (o.userSelectedTargets || []).forEach(t => set.add(t)));
    return Array.from(set);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders.slice();
    if (providerFilter !== 'all') {
      list = list.filter(o => (o.providers || []).some(p => String(p.providerId || '').toLowerCase().includes(providerFilter)));
    }
    if (targetFilter !== 'all') {
      list = list.filter(o => Array.isArray(o.userSelectedTargets) && o.userSelectedTargets.includes(targetFilter));
    }
    if (statusFilter !== 'all') {
      list = list.filter(o => {
        const js = jobsByOrderId[o.id] || [];
        if (js.length === 0) return false; // cannot determine yet
        const completed = js.every(j => j.status === 'completed');
        const processing = js.some(j => j.status === 'processing');
        const failed = js.some(j => j.status === 'failed' || j.status === 'cancelled');
        return statusFilter === 'completed' ? completed : statusFilter === 'processing' ? processing : failed;
      });
    }
    // Sorting
    list.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      const jobsA = (jobsByOrderId[a.id] || []);
      const jobsB = (jobsByOrderId[b.id] || []);
      const costA = jobsA.reduce((s, j) => s + (j.actualCost || 0), 0);
      const costB = jobsB.reduce((s, j) => s + (j.actualCost || 0), 0);
      switch (sortBy) {
        case 'date_asc': return dateA - dateB;
        case 'jobs_desc': return jobsB.length - jobsA.length;
        case 'jobs_asc': return jobsA.length - jobsB.length;
        case 'cost_desc': return costB - costA;
        case 'cost_asc': return costA - costB;
        case 'date_desc':
        default: return dateB - dateA;
      }
    });
    return list;
  }, [orders, jobsByOrderId, providerFilter, statusFilter, targetFilter, sortBy]);

  const containerRef = useRef(null);

  // Note: Focus management and scroll position tracking removed since we now use full-window scrolling
  // The scroll position is managed by the main modal container

  return (
    <div
      ref={containerRef}
      className="orders-list"
      role="region"
      aria-label={`Analysis results, ${filteredOrders.length} orders`}
      aria-live="polite"
      aria-atomic="false"
    >
      {/* Filter Bar - Conditionally rendered */}
      {showFilterBar && (
        <div className="orders-filter-container">
          <button 
            className="filter-toggle-btn"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            aria-expanded={filtersExpanded}
            aria-controls="orders-filter-controls"
          >
            <span className="filter-toggle-icon">{filtersExpanded ? '▼' : '▶'}</span>
            <span className="filter-toggle-text">Filters</span>
            {!filtersExpanded && (
              <span className="filter-summary">
                {providerFilter !== 'all' || targetFilter !== 'all' || statusFilter !== 'all' 
                  ? '•' 
                  : ''} {sortBy === 'date_desc' ? 'Newest' : sortBy === 'date_asc' ? 'Oldest' : 'Custom'}
              </span>
            )}
          </button>
          
          {filtersExpanded && (
            <div 
              id="orders-filter-controls"
              className="orders-filter-bar"
              role="toolbar"
              aria-label="Filter and sort analysis results"
            >
              <div className="filter-group">
                <label className="filter-label">
                  Provider
                  <select 
                    value={providerFilter} 
                    onChange={(e)=>setProviderFilter(e.target.value)} 
                    className="filter-select"
                  >
                    <option value="all">All</option>
                    {providerOptions.map(opt => (
                      <option key={opt} value={opt}>{opt.replaceAll('-', ' ')}</option>
                    ))}
                  </select>
                </label>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">
                  Targets
                  <select 
                    value={targetFilter} 
                    onChange={(e)=>setTargetFilter(e.target.value)} 
                    className="filter-select"
                  >
                    <option value="all">All</option>
                    {targetOptions.map(t => (
                      <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>
                    ))}
                  </select>
                </label>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">
                  Status
                  <select 
                    value={statusFilter} 
                    onChange={(e)=>setStatusFilter(e.target.value)} 
                    className="filter-select"
                  >
                    <option value="all">All</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </label>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">
                  Sort
                  <select 
                    value={sortBy} 
                    onChange={(e)=>setSortBy(e.target.value)} 
                    className="filter-select"
                  >
                    <option value="date_desc">Newest</option>
                    <option value="date_asc">Oldest</option>
                    <option value="jobs_desc">Jobs (many→few)</option>
                    <option value="jobs_asc">Jobs (few→many)</option>
                    <option value="cost_desc">Cost (high→low)</option>
                    <option value="cost_asc">Cost (low→high)</option>
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {filteredOrders.map((o) => {
        const isExpanded = !!expandedOrders[o.id];
        const orderJobs = jobsByOrderId[o.id] || [];
        const vm = buildOrderViewModel(o, orderJobs);
        return (
          <div key={o.id}>
            <OrderCard
              orderVm={vm}
              isExpanded={isExpanded}
              onToggle={async () => {
                const next = !isExpanded;
                onToggleExpand(o.id);
                if (next && !jobsByOrderId[o.id]) {
                  await onEnsureJobsLoaded(o.id);
                }
              }}
              onOpen={() => onOpenOrder(o.id)}
              size="regular"
            />
            {isExpanded && (
              <div className="order-jobs-container">
                {jobsByOrderId[o.id] ? (
                  jobsByOrderId[o.id].length > 0 ? (
                    jobsByOrderId[o.id].map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        resultsMeta={resultsByJobId[job.id]}
                        size="regular"
                        onOpen={() => onOpenJob(o.id, job.id)}
                      />
                    ))
                  ) : (
                    <div className="no-jobs-message">No jobs found for this order.</div>
                  )
                ) : (
                  <JobsListSkeleton count={3} size="compact" />
                )}
              </div>
            )}
          </div>
        );
      })}
      {isLoading && (
        <OrderCardSkeleton />
      )}
      
      <div className="load-more-section">
        {!isLoading && hasMore ? (
          <button className="btn-secondary" onMouseDown={(e)=>{ e.stopPropagation(); onLoadMore(); }}>Load more</button>
        ) : !isLoading && !hasMore ? (
          <div className="no-more-message">No more orders</div>
        ) : null}
      </div>
    </div>
  );
};

export default OrdersListView;


